import React from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";

// Create / edit an agent login. The User ID is what the agent types on the
// agent panel login screen, so it is immutable-ish: editable, but a change
// means the agent's existing credentials stop working, hence the hint.

const TIME_RE = /^([01]?\d|2[0-3]):[0-5]\d$/;

const schema = (isEdit) =>
  Yup.object({
    userid: Yup.string().trim().min(3, "At least 3 characters").required("User ID is required"),
    password: isEdit
      ? Yup.string().test(
          "len",
          "At least 6 characters",
          (v) => !v || v.length >= 6
        )
      : Yup.string().min(6, "At least 6 characters").required("Password is required"),
    name: Yup.string().trim().max(80, "Too long"),
    email: Yup.string().trim().email("Enter a valid email"),
    phone: Yup.string().trim().max(20, "Too long"),
    employeeCode: Yup.string().trim().max(30, "Too long"),
    team: Yup.string().trim().max(40, "Too long"),
    shiftStart: Yup.string().test("t", "Use HH:mm, e.g. 09:30", (v) => !v || TIME_RE.test(v)),
    shiftEnd: Yup.string().test("t", "Use HH:mm, e.g. 18:30", (v) => !v || TIME_RE.test(v)),
  });

export default function AgentForm({ open, onClose, onSubmit, agent, saving }) {
  const isEdit = Boolean(agent?.id);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      userid: agent?.userid || "",
      password: "",
      name: agent?.name || "",
      email: agent?.email || "",
      phone: agent?.phone || "",
      employeeCode: agent?.employeeCode || "",
      team: agent?.team || "",
      shiftStart: agent?.shiftStart || "",
      shiftEnd: agent?.shiftEnd || "",
      status: agent?.status === "inactive" ? "inactive" : "active",
    },
    validationSchema: schema(isEdit),
    onSubmit: (values) => {
      const payload = { ...values };
      if (isEdit && !payload.password) delete payload.password;
      onSubmit(payload, formik.resetForm);
    },
  });

  const field = (name, label, extra = {}) => (
    <TextField
      fullWidth
      size="small"
      name={name}
      label={label}
      value={formik.values[name]}
      onChange={formik.handleChange}
      onBlur={formik.handleBlur}
      error={formik.touched[name] && Boolean(formik.errors[name])}
      helperText={(formik.touched[name] && formik.errors[name]) || extra.hint || " "}
      {...extra.props}
    />
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={formik.handleSubmit}>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {isEdit ? "Edit Agent" : "Create New Agent"}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ color: "#6b7280", mb: 2 }}>
            These are the credentials the agent uses to sign in at{" "}
            <strong>/agent/login</strong>.
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              {field("userid", "User ID *", {
                hint: isEdit ? "Changing this changes their login" : "Used to log in",
              })}
            </Grid>
            <Grid item xs={12} sm={6}>
              {field("password", isEdit ? "New Password" : "Password *", {
                hint: isEdit ? "Leave blank to keep the current one" : "At least 6 characters",
                props: { type: "password", autoComplete: "new-password" },
              })}
            </Grid>

            <Grid item xs={12} sm={6}>
              {field("name", "Full Name")}
            </Grid>
            <Grid item xs={12} sm={6}>
              {field("employeeCode", "Employee Code")}
            </Grid>

            <Grid item xs={12} sm={6}>
              {field("email", "Email")}
            </Grid>
            <Grid item xs={12} sm={6}>
              {field("phone", "Phone")}
            </Grid>

            <Grid item xs={12} sm={6}>
              {field("team", "Team / Process")}
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                size="small"
                name="status"
                label="Status"
                value={formik.values.status}
                onChange={formik.handleChange}
                helperText="Inactive agents cannot log in"
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              {field("shiftStart", "Shift Start", { hint: "HH:mm - used to flag late logins" })}
            </Grid>
            <Grid item xs={12} sm={6}>
              {field("shiftEnd", "Shift End", { hint: "HH:mm" })}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} sx={{ color: "#6b7280" }}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={saving}
            sx={{ bgcolor: "#0C237B", "&:hover": { bgcolor: "#1a3399" }, borderRadius: "8px" }}
          >
            {saving ? "Saving..." : isEdit ? "Save Changes" : "Create Agent"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
